import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CookieStorageService } from '@df/browser/storage/cookie-storage.service';
import { LocalStorage } from '@df/browser/storage/decorator/local-storage';
import { LocalStorageService } from '@df/browser/storage/local-storage.service';
import { RecentlyViewedService } from '@df/catalog-ui/recently-viewed/recently-viewed.service';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { Logger } from '@df/core/service/logger';
import { WINDOW } from '@df/dom/globals';
import { TimeoutService } from '@df/ng/timeout.service';
import cloneDeep from 'lodash-es/cloneDeep';
import defaultsDeep from 'lodash-es/defaultsDeep';
import without from 'lodash-es/without';
import { Observable, lastValueFrom, map, of } from 'rxjs';
import { throwError } from 'rxjs/internal/observable/throwError';
import { catchError } from 'rxjs/internal/operators/catchError';
import { AdvancedCommerce } from './advanced-commerce.namespace';
import { AdvancedCommerceConfigStore } from './store/advanced-commerce-config.store';

@Injectable({
    providedIn: 'root'
})
export class AdvancedCommerceService {
    protected configStore = inject(AdvancedCommerceConfigStore);
    protected logger = inject(Logger);
    protected meta = inject(Meta);
    protected localStorageService = inject(LocalStorageService);
    protected recentService = inject(RecentlyViewedService);
    protected cookie = inject(CookieStorageService);
    protected window = inject(WINDOW);
    protected http = inject(HttpClient);
    protected timeoutService = inject(TimeoutService);
    protected app = inject(App);

    @LocalStorage('_ac.prevSearches')
    previousSearches: string[] | undefined = [];

    readonly include = ['settings', 'labels', 'redirects'];
    readonly fieldsProducts = ['id', 'titles', 'url', 'type', '_features'];

    query<T>(data: Partial<AdvancedCommerce.Query.RequestData>) {
        const args: Record<string, boolean | undefined> = {};

        if (this.app.customer.model.isLoggedIn && this.app.customer.model.data) {
            if (this.app.customer.model.data.volunteer !== undefined) {
                args['volunteer'] = !!this.app.customer.model.data.volunteer;
            }
            if (this.app.customer.model.data.general_parent !== undefined) {
                args['general_parent'] = !!this.app.customer.model.data.general_parent;
            }
            if (this.app.customer.model.data.squirrels_parent !== undefined) {
                args['squirrels_parent'] = !!this.app.customer.model.data.squirrels_parent;
            }
            if (this.app.customer.model.data.beavers_parent !== undefined) {
                args['beavers_parent'] = !!this.app.customer.model.data.beavers_parent;
            }
            if (this.app.customer.model.data.cubs_parent !== undefined) {
                args['cubs_parent'] = !!this.app.customer.model.data.cubs_parent;
            }
            if (this.app.customer.model.data.scouts_parent !== undefined) {
                args['scouts_parent'] = !!this.app.customer.model.data.scouts_parent;
            }
        }

        const postData: AdvancedCommerce.Query.RequestData = defaultsDeep(cloneDeep(data), {
            format: 'json',
            include: this.include,
            response_options: {
                fields: {
                    products: this.fieldsProducts
                }
            },
            args,
            previous_searches: this.previousSearches,
            ...this.additionalPostData,
            recently_viewed: this.recentService.collection?.map(id => `${id}`) // map to string
        });

        return this.post(postData, AdvancedCommerce.Api.Type.query) as Observable<T>;
    }

    /**
     *
     * @param data
     * @param type
     * @returns
     */
    post(data: AdvancedCommerce.Request, type: AdvancedCommerce.Api.Type) {
        const httpOptions = {
            headers: this.httpHeaders
        };

        const url = this.getApiUrl(type);
        const postData: AdvancedCommerce.Query.RequestData = defaultsDeep(data, {
            user: {
                uid: this.uid,
                sid: this.sid
            },
            context: {
                ...this.context
            },
            ...this.additionalPostData
        });

        if (url !== undefined) {
            return this.http.post<AdvancedCommerce.Api.Response>(url, postData, httpOptions).pipe(
                map((response: AdvancedCommerce.Api.Response) => this.patchUser(response)),
                catchError((error: HttpErrorResponse) => this.handleError(error))
            );
        }

        return of();
    }

    get additionalPostData(): Object {
        return {};
    }

    get context(): AdvancedCommerce.Api.Context {
        const model = this.meta.model;
        const data = {
            page_url: this.pageUrl,
            user_agent: this.userAgent,
            language_id: this.configStore.config()?.languageId || 'en-gb',
            currency_id: this.app.data.currency.base || 'GBP'
        };

        if (this.configStore.config()?.warehouseId) {
            data['warehouse_id'] = this.configStore.config()?.warehouseId;
        }

        if (model) {
            switch (model.type) {
                case 'category':
                    return {
                        category_id: `${model.id}`,
                        ...data
                    };
                case 'product':
                    return {
                        product_id: `${(model as any)?.parent.id || model.id}`,
                        ...data
                    };
                case 'page':
                    return {
                        page_id: `${model.id}`,
                        ...data
                    };
            }
        }

        return data;
    }

    get pageUrl(): string {
        return this.window.location.href;
    }

    get userAgent(): string {
        return this.window.navigator.userAgent;
    }

    /**
     * Get headers
     *
     * @returns HttpHeaders
     */
    protected get httpHeaders(): HttpHeaders {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Basic ${this.configStore.token()}`
        });
    }

    /**
     * Get API URL
     *
     * @param type - Type of API
     * @returns string | null
     */
    protected getApiUrl(type: AdvancedCommerce.Api.Type): string | undefined {
        if (!this.configStore || this.configStore.config() === undefined) {
            return undefined;
        }

        const domainKey = this.configStore.config()?.domainKey;
        const locationKey = this.configStore.config()?.locationKey ? `-${this.configStore.config()?.locationKey}` : '';

        if (!domainKey || !this.configStore.config()?.enabled) {
            return undefined;
        }

        return `https://${domainKey}.store${locationKey}.advancedcommerce.services/api/js/${type}`;
    }

    protected handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            // A client-side or network error occurred. Handle it accordingly.
            this.logger.error('An error occurred:', error.error.message);
        } else {
            // The backend returned an unsuccessful response code.
            // The response body may contain clues as to what went wrong,
            this.logger.error(`Backend returned code ${error.status}, ` + `body was: ${error.error}`);
        }

        // return an observable with a user-facing error message
        return throwError(() => new Error(error.message || 'Something bad happened; please try again later.'));
    }

    protected patchUser(response: AdvancedCommerce.Api.Response) {
        if (response['user']) {
            if (response['user']['uid']) {
                this.uid = response['user']['uid'];
            }

            if (response['user']['sid']) {
                this.sid = response['user']['sid'];
            }
        }

        return response;
    }

    protected set uid(value: string) {
        this.cookie.set('_ac.uid', value);
    }

    protected get uid(): string | null {
        return this.cookie.get('_ac.uid') || null;
    }

    protected set sid(value: string) {
        this.cookie.set('_ac.sid', value);
    }

    protected get sid(): string | null {
        return this.cookie.get('_ac.sid') || null;
    }

    /**
     * Set previous searches.
     * @param term {string} - search term
     */
    setPreviousSearches(term: string): void {
        if (term) {
            const previousSearches = without(this.previousSearches, term);

            previousSearches.unshift(term);

            this.previousSearches = previousSearches.splice(0, 15);
        }
    }

    /**
     * Get recommendations
     *
     * @param options {AdvancedCommerce.Engine.Recommendations.Options} - options
     * @param ids {number[]} - ids
     * @param full {boolean} - full
     * @returns {Promise<T>}
     */
    getRecommendations<T = number[] | AdvancedCommerce.Query.Response>(
        options: AdvancedCommerce.Engine.Recommendations.Options,
        ids: number[],
        full: boolean = false
    ): Promise<T> {
        return new Promise(async resolve => {
            const elements = Array.isArray(options.elements) ? options.elements : [options.elements];
            const data: Partial<AdvancedCommerce.Query.RequestData> = {
                elements,
                context: {
                    ...options.context
                }
            };

            if (ids?.length > 0) {
                if (!data.context) {
                    data.context = {};
                }

                data.context.product_id = `${ids.join(',')}`;
            } else {
                delete data?.context?.product_id;
            }

            lastValueFrom(this.query<AdvancedCommerce.Query.Response>(data)).then(response => {
                const ids = response?.elements?.[elements[0]]?.['products']?.results.map(result => Number(result.item.id)) || [];

                resolve(full ? (response as unknown as T) : (ids as unknown as T));
            });
        });
    }
}

export function joinWithSlash(start: string, end: string): string {
    if (start.length == 0) {
        return end;
    }
    if (end.length == 0) {
        return start;
    }
    let slashes = 0;
    if (start.endsWith('/')) {
        slashes++;
    }
    if (end.startsWith('/')) {
        slashes++;
    }
    if (slashes == 2) {
        return start + end.substring(1);
    }
    if (slashes == 1) {
        return start + end;
    }
    return start + '/' + end;
}
